const processEvent = require('analytics/processEvent');
const anaConsts = require('analytics/constants');
const deepExtend = require('utils/deepExtend');
const shouldServiceRun = require('utils/Services').shouldServiceRun;
const store = require('Store');
const watch = require('redux-watch');
const certonaUtils = require('utils/certona.js');
const certonaConstants = require('utils/certonaConstants');
const updateCarousel = require('actions/CertonaActions').updateCarousel;
const userUtils = require('utils/User');
const skuUtils = require('utils/Sku');
const basketUtils = require('utils/Basket');
const anaUtils = require('analytics/utils');
const localeUtils = require('utils/LanguageLocale');
const { Application } = require('utils/framework');
const {
    EventType, Certona, CertonaReady, CertonaLoaded, UserInfoReady, UserInfoLoaded
} = require('constants/events');

window.addEventListener(CertonaReady, () => Application.events.dispatchServiceEvent(Certona, EventType.ServiceCtrlrsApplied, true));

function setCertonaObject(prop, value) {
    window.certona[prop] = value;
}

const updateCertonaCustomerId = function (user) {
    if (user && user.profileStatus >= 0) {
        setCertonaObject('customerid', user.profileId);
    }
};

const updateSkuId = function (skuId) {
    setCertonaObject('itemid', skuId);
};

const updateCategoryId = function (categoryId) {
    setCertonaObject('categoryid', categoryId);
};

const updateBasketItems = (basket = {}) => {
    const isUserSubscribed = userUtils.isFlash() || userUtils.isRouge();
    const deltaAmount = basket.remainToFreeShipping;
    const removeCurrency = deltaAmount ? basketUtils.removeCurrency(deltaAmount) : null;
    const shippingAmount = isUserSubscribed ? '0' : removeCurrency || '0';

    setCertonaObject('storeid', basket?.pickupBasket?.storeDetails?.storeId);
    setCertonaObject('itemid', '');
    setCertonaObject('freeshipping', shippingAmount);
    setCertonaObject('isflash', userUtils.isFlash());

    if (basket.itemCount) {
        setCertonaObject('itemid', certonaUtils.getSkuIdsInBasket(basket.products));
    }
};

var pageType;

const getBasket = () =>
    new Promise(resolve => {
        if (pageType === certonaConstants.PAGE_TYPES.PICK_UP_CART) {
            const basket = store.getState().basket;

            if (basket.isInitialized) {
                updateBasketItems(basket);
                resolve();
            } else {
                const unsubscribe = store.subscribe(
                    watch(
                        store.getState,
                        'basket'
                    )(newVal => {
                        if (newVal.isInitialized) {
                            updateBasketItems(newVal);
                            unsubscribe();
                            resolve();
                        }
                    }),
                    { ignoreAutoUnsubscribe: true }
                );
            }
        } else {
            resolve();
        }
    });

const getProduct = () =>
    new Promise(resolve => {
        if (pageType === certonaConstants.PAGE_TYPES.PRODUCT) {
            // let { currentProduct = {} } = store.getState().product;
            const currentProduct = store.getState().page.product;
            const skuIdParam = Sephora.Util.getQueryStringParams().skuId;

            // Set itemid with skuid in url or fallback and set with default skuid
            if (skuIdParam) {
                updateSkuId(skuIdParam[0]);
                resolve();
            } else if (currentProduct.currentSku && currentProduct.currentSku.skuId) {
                updateSkuId(currentProduct.currentSku.skuId);
                resolve();
            } else {
                const unsubscribe = store.subscribe(
                    watch(
                        store.getState,
                        'page.product'
                    )(newVal => {
                        if (newVal.currentSku && newVal.currentSku.skuId) {
                            updateSkuId(newVal.currentSku.skuId);
                            unsubscribe();
                            resolve();
                        }
                    }),
                    { ignoreAutoUnsubscribe: true }
                );
            }
        } else {
            resolve();
        }
    });

const updateCertonaCustomerIdIfUserExists = (user, callback) => {
    if (user && (user.profileId || user.profileLocale)) {
        updateCertonaCustomerId(user);
        callback();

        return true;
    }

    return false;
};

const getUser = () =>
    new Promise(resolve => {
        if (certonaUtils.shouldSetCustomerId()) {
            // Listen to the UserInfoLoaded event and set the data from the service result
            // if hydration is still in progress. Otherwise - do nothing and let the store
            // to handle data later on
            Application.events.onLastLoadEvent(window, [UserInfoLoaded], () => {
                if (!Sephora.Util.InflatorComps.services?.loadEvents?.HydrationFinished) {
                    const user = Sephora.Util.InflatorComps.services.UserInfo?.data?.profile;
                    updateCertonaCustomerIdIfUserExists(user, resolve);
                } else {
                    const user = store.getState().user;

                    // Try to set a user from the store
                    const userHasBeenSet = user?.isInitialized && updateCertonaCustomerIdIfUserExists(user, resolve);

                    // And if a user couldn't be updated
                    if (!userHasBeenSet) {
                        // Wait for a user to be set in the store
                        const unsubscribe = store.subscribe(
                            watch(
                                store.getState,
                                'user'
                            )(newVal => {
                                // and set it again if hasn't been set before
                                if (!userHasBeenSet) {
                                    updateCertonaCustomerIdIfUserExists(newVal, () => {
                                        unsubscribe();
                                        resolve();
                                    });
                                }
                            }),
                            { ignoreAutoUnsubscribe: true }
                        );
                    }
                }
            });
        } else {
            resolve();
        }
    });

const getCategory = () =>
    new Promise(resolve => {
        if (pageType === certonaConstants.PAGE_TYPES.CATEGORY) {
            const categoryId = store.getState().category.currentCategory;

            if (categoryId) {
                updateCategoryId(categoryId);
                resolve();
            } else {
                const unsubscribe = store.subscribe(
                    watch(
                        store.getState,
                        'category.currentCategory'
                    )(newVal => {
                        if (newVal) {
                            updateCategoryId(newVal);
                            unsubscribe();
                            resolve();
                        }
                    }),
                    { ignoreAutoUnsubscribe: true }
                );
            }
        } else {
            resolve();
        }
    });

const getNullSearchTerm = () =>
    new Promise(resolve => {
        if (pageType === certonaConstants.PAGE_TYPES.NO_SEARCH) {
            const queryParams = Sephora.Util.getQueryStringParams();
            let keyword = '';

            if (queryParams.keyword && queryParams.keyword.length) {
                keyword = queryParams.keyword[0];
            }

            setCertonaObject('searchTerm', keyword);
            resolve();
        } else {
            resolve();
        }
    });

const updateConfirmationItems = function (order) {
    const orderItems = (order && order.items && order.items.items) || [];
    setCertonaObject(
        'itemid',
        orderItems
            .map(function (item) {
                return item.sku.skuId;
            })
            .join(';')
    );
    setCertonaObject(
        'qty',
        orderItems
            .map(function (item) {
                return item.qty;
            })
            .join(';')
    );

    //iterate through orderItems and create an array with prices of all items
    //with actual cost. do not include free, birthday gift, or reward items.
    const priceArray = [];
    let price;
    let priceInDigits;
    let priceLocale;
    orderItems.forEach(orderItem => {
        if (!skuUtils.isFree(orderItem.sku) && !skuUtils.isBiRewardGwpSample(orderItem.sku)) {
            price = orderItem.salePriceSubtotal ? orderItem.salePriceSubtotal : orderItem.listPriceSubtotal;
            priceInDigits = price.replace(/[^\d.]/g, '');
            priceLocale = localeUtils.isCanada() ? priceInDigits : anaUtils.convertToUSD(priceInDigits);
            priceArray.push(priceLocale);
        } else {
            priceArray.push('0');
        }
    });
    setCertonaObject('price', priceArray.join(';'));
    const orderTotal = priceArray.length > 1 ? priceArray.reduce((a, b) => parseFloat(a) + parseFloat(b)).toFixed(2) : priceArray[0];
    setCertonaObject('total', orderTotal);
    setCertonaObject('transactionid', order.header.orderId);
};

const getPurchase = () =>
    new Promise(resolve => {
        if (pageType === certonaConstants.PAGE_TYPES.PURCHASE) {
            const { orderDetails = {} } = store.getState().order;

            if (orderDetails.isInitialized) {
                Sephora.analytics.promises.initialPageLoadFired.then(() => {
                    updateConfirmationItems(orderDetails);
                    resolve();
                });
            } else {
                const unsubscribe = store.subscribe(
                    watch(
                        store.getState,
                        'order.orderDetails'
                    )(order => {
                        if (order.isInitialized) {
                            Sephora.analytics.promises.initialPageLoadFired.then(() => {
                                updateConfirmationItems(order);
                                unsubscribe();
                                resolve();
                            });
                        }
                    }),
                    { ignoreAutoUnsubscribe: true }
                );
            }
        } else {
            resolve();
        }
    });

const updateContentStoreItems = function (certonaObject) {
    setCertonaObject('categoryid', certonaObject.categoryid);
    setCertonaObject('pagetype', certonaObject.certonaPageType);
};

const updateStoreId = function (preferredStoreInfo) {
    setCertonaObject('storeid', preferredStoreInfo.storeId);
};

const getContentPage = () =>
    new Promise((resolve, reject) => {
        if (
            pageType === certonaConstants.PAGE_TYPES.CONTENT &&
            pageType !== certonaConstants.PAGE_TYPES.HOLIDAY &&
            pageType !== certonaConstants.PAGE_TYPES.ATRISKLAPSED &&
            pageType !== certonaConstants.PAGE_TYPES.CONVENIENCEHUB &&
            pageType !== certonaConstants.PAGE_TYPES.SAVINGSEVENT
        ) {
            const productRecs = store.getState().productRecs;

            if (productRecs.certonaObject.certonaPageType) {
                updateContentStoreItems(productRecs.certonaObject);
                resolve();
            } else {
                const unsubscribe = store.subscribe(
                    watch(
                        store.getState,
                        'productRecs.certonaObject'
                    )(certonaObject => {
                        if (certonaObject.certonaPageType) {
                            updateContentStoreItems(certonaObject);
                            unsubscribe();
                            resolve();
                        } else {
                            // eslint-disable-next-line prefer-promise-reject-errors
                            reject();
                        }
                    }),
                    { ignoreAutoUnsubscribe: true }
                );
            }
        } else {
            if (pageType === certonaConstants.PAGE_TYPES.CONVENIENCEHUB) {
                const unsubscribe = store.subscribe(
                    watch(
                        store.getState,
                        'user.preferredStoreInfo'
                    )(preferredStoreInfo => {
                        if (preferredStoreInfo.storeId !== null) {
                            updateStoreId(preferredStoreInfo);
                            unsubscribe();
                            resolve();
                        } else {
                            // eslint-disable-next-line prefer-promise-reject-errors
                            reject();
                        }
                    }),
                    { ignoreAutoUnsubscribe: true }
                );
            } else {
                resolve();
            }
        }
    });

const getLovesListPage = () =>
    new Promise(resolve => {
        if (pageType === certonaConstants.PAGE_TYPES.WISH_LIST) {
            const loves = store.getState().loves;

            // loves.shareLink is only defined if store is set up
            if (loves.shareLink) {
                setCertonaObject('itemid', loves.shoppingListIds.join(';'));
                resolve();
            } else {
                const unsubscribe = store.subscribe(
                    watch(
                        store.getState,
                        'loves'
                    )(newVal => {
                        if (newVal && newVal.shoppingListIds) {
                            setCertonaObject('itemid', newVal.shoppingListIds.join(';'));
                            unsubscribe();
                            resolve();
                        }
                    }),
                    { ignoreAutoUnsubscribe: true }
                );
            }
        } else {
            resolve();
        }
    });

const getNthCategoryPage = () =>
    new Promise((resolve, reject) => {
        if (pageType === certonaConstants.PAGE_TYPES.NTH_CATEGORY) {
            const productRecs = store.getState().productRecs;

            if (productRecs.certonaObject.certonaPageType) {
                setCertonaObject('pagetype', productRecs.certonaObject.certonaPageType);
                resolve();
            } else {
                const unsubscribe = store.subscribe(
                    watch(
                        store.getState,
                        'productRecs.certonaObject'
                    )(certonaObject => {
                        if (certonaObject.certonaPageType) {
                            setCertonaObject('pagetype', certonaObject.certonaPageType);
                            unsubscribe();
                            resolve();
                        } else {
                            // eslint-disable-next-line prefer-promise-reject-errors
                            reject();
                        }
                    }),
                    { ignoreAutoUnsubscribe: true }
                );
            }
        } else {
            resolve();
        }
    });

const getCertona = () => new Promise(resolve => Application.events.onLastLoadEvent(window, [CertonaLoaded], resolve));

/**
 * This function is called from Certona only.
 *
 * If you want to re-initiate this call with fresh recommendations from Sephora side,
 * use function: certonaResx.run() //TODO: change to callCertona() if lib is updated
 */
Sephora.certona = function (recs) {
    // Someone is clearing recs with object.assign, I need to save a copy for later
    const savedRecsForAnalytics = deepExtend({}, recs);

    if (digitalData.page.attributes.initialPageLoadDidOccur) {
        // require.ensure is used to package nested requires in components.chunk.js
        // rather than priority.bundle.js in order to keep priority.bundle.js lean
        require.ensure(
            [],
            function (require) {
                const certonaImpression = require('analytics/bindings/pages/all/certonaImpression');

                try {
                    const isShown =
                        savedRecsForAnalytics.resonance &&
                        savedRecsForAnalytics.resonance.schemes &&
                        savedRecsForAnalytics.resonance.schemes.length &&
                        savedRecsForAnalytics.resonance.schemes.filter(scheme => {
                            return scheme.display === 'yes';
                        });
                    const orderDetails = store.getState().order.orderDetails;
                    const isRopisOrder = orderDetails.header && orderDetails.header.isRopisOrder;

                    if (isShown && !isRopisOrder) {
                        processEvent.process(
                            anaConsts.LINK_TRACKING_EVENT,
                            {
                                data: {
                                    recs: savedRecsForAnalytics,
                                    bindingMethods: [certonaImpression],
                                    eventStrings: [anaConsts.Event.EVENT_71],
                                    specificEventName: anaConsts.EVENT_NAMES.CERTONA_IMPRESSION
                                }
                            },
                            { specificEventName: anaConsts.EVENT_NAMES.CERTONA_IMPRESSION }
                        );
                    }
                } catch (e) {
                    certonaUtils.fireCertonaFailed();
                    // eslint-disable-next-line no-console
                    console.error(`Exception processing link tracking event
                in Certona service: ${e}`);
                }
            },
            'components'
        );
    }

    store.dispatch(updateCarousel(recs));
    certonaUtils.fireCertonaReady();
};

Application.events.onLastLoadEvent(window, [UserInfoReady], () => {
    store.subscribe(
        watch(
            store.getState,
            'user.profileId'
        )(profileId => {
            certonaUtils.sendCertona(certonaConstants.CERTONA_EVENTS.USER_CHANGED, { customerId: profileId });
        }),
        { ignoreAutoUnsubscribe: true }
    );
});

const setupCertona = () => {
    /* Stop service from loading if not necessary */
    if (!shouldServiceRun.certona()) {
        return;
    }

    /**
     * Homepage setup for Certona
     * TODO: update environment check once BE sets up the flag
     */

    const currentLanguage =
        localeUtils.getCurrentLanguage().toUpperCase() === localeUtils.LANGUAGES.EN ? localeUtils.LANGUAGES.ENGLISH : localeUtils.LANGUAGES.FRENCH;

    window.certona = {
        pagetype: certonaUtils.getPageType(),
        country: localeUtils.getCurrentCountry().toUpperCase(),
        language: currentLanguage,
        device: Sephora.isDesktop() ? 'DESKTOP' : 'MOBILE'
    };

    if (certonaUtils.pageHasRecommendations()) {
        setCertonaObject('recommendations', true);
    }

    pageType = certonaUtils.getPageType();

    Promise.all([
        getCertona(),
        getBasket(),
        getProduct(),
        getUser(),
        getCategory(),
        getPurchase(),
        getContentPage(),
        getNullSearchTerm(),
        getLovesListPage(),
        getNthCategoryPage()
    ]).then(certonaUtils.initCertona);
};

module.exports = setupCertona;
